import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { fetchFaqsById, fetchFaqs, Faq } from '@/api/faq';
import NewsAttachIcon from '@/assets/icon/news-attach.svg?react';
import NewsKakaoIcon from '@/assets/icon/news-kakao.svg?react';
import { formatDateTime } from '@/utils/dateUtils';
import { Helmet } from 'react-helmet-async';

const FaqDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    window.scrollTo({ top: 0 });
    queryClient.invalidateQueries({ queryKey: ['faq', id] });
  }, [id, queryClient]);

  const {
    data: faq,
    isLoading,
    isError,
  } = useQuery<Faq>({
    queryKey: ['faq', id],
    queryFn: () => fetchFaqsById(Number(id)),
    enabled: !!id,
  });

  const { data: faqs } = useQuery<Faq[]>({
    queryKey: ['Faqs'],
    queryFn: fetchFaqs,
  });

  const recommended = [...(faqs || [])]
    .filter((item) => String(item.id) !== id)
    .reverse()
    .slice(0, 4);

  if (isLoading) return <StatusText>불러오는 중...</StatusText>;
  if (isError || !faq) return <StatusText>자주 묻는 질문을 불러올 수 없습니다.</StatusText>;

  const formattedDate = formatDateTime(faq.createdAt);

  return (
    <>
      {/* ✅ SEO 메타태그 추가 */}
      <Helmet>
        <title>{`${faq.question} | 자주 묻는 질문 - 올받음`}</title>
        <meta name="description" content={faq.answer.replace(/<[^>]+>/g, '').slice(0, 1000)} />
        <meta property="og:title" content={`${faq.question} | 올받음`} />
        <meta
          property="og:description"
          content={faq.answer.replace(/<[^>]+>/g, '').slice(0, 1000)}
        />
      </Helmet>
      <Outer>
        <Wrapper>
          <HeaderSection>
            <DateText>{formattedDate}</DateText>
            <TitleText>{faq.question}</TitleText>
          </HeaderSection>

          <Divider />

          <ContentSection dangerouslySetInnerHTML={{ __html: faq.answer }} />

          <AttachmentSection>
            <IconRow>
              <NewsKakaoIcon
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  window.Kakao?.Link?.sendDefault({
                    objectType: 'feed',
                    content: {
                      title: faq.question,
                      description: faq.answer.replace(/<[^>]+>/g, '').slice(0, 100),
                      imageUrl: 'https://www.hiadjuster.com/allbadeumIcon.svg',
                      link: {
                        mobileWebUrl: window.location.href,
                        webUrl: window.location.href,
                      },
                    },
                    buttons: [
                      {
                        title: '웹으로 보기',
                        link: {
                          mobileWebUrl: window.location.href,
                          webUrl: window.location.href,
                        },
                      },
                    ],
                  })
                }
              />
              <NewsAttachIcon
                style={{ cursor: 'pointer' }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    alert('링크가 복사되었습니다.');
                  } catch (err) {
                    console.error('복사 실패:', err);
                  }
                }}
              />
            </IconRow>
          </AttachmentSection>

          <ButtonRow>
            <BackButton onClick={() => navigate('/intro/faq')}>
              <ButtonText>목록 보기</ButtonText>
            </BackButton>
          </ButtonRow>

          <Divider />

          <SubTitle>추천 질문</SubTitle>
          <RecommendationWrapper>
            {recommended.map((item, index) => (
              <RecommendItem key={item.id} onClick={() => navigate(`/intro/faq/${item.id}`)}>
                <LeftBox>
                  <RecommendIndex>{String(index + 1).padStart(2, '0')}</RecommendIndex>
                </LeftBox>
                <RightText>{item.question}</RightText>
              </RecommendItem>
            ))}
          </RecommendationWrapper>
        </Wrapper>
      </Outer>
    </>
  );
};

export default FaqDetailPage;

// Styled Components
const Outer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 630px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 156px;
  padding: 0 16px;

  @media (max-width: 768px) {
    margin-top: 120px;
    padding: 0 12px;
    gap: 12px;
  }
`;

const HeaderSection = styled.div`
  align-self: stretch;
  text-align: center;
`;

const DateText = styled.div`
  color: var(--gray-ui_gray02, #252525);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 14px;
    letter-spacing: -0.28px;
  }
`;

const TitleText = styled.div`
  margin-top: 8px;
  color: var(--gray-ui_gray02, #252525);
  font-size: 32px;
  font-weight: 700;
  line-height: 130%;
  letter-spacing: -0.64px;

  @media (max-width: 768px) {
    font-size: 22px;
    letter-spacing: -0.4px;
  }
`;

const Divider = styled.hr`
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px solid #000;
  margin: 50px 0;
`;

const ContentSection = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 16px;
  font-weight: 400;
  line-height: 180%;
  letter-spacing: 1%;

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    gap: 16px;
  }
`;

const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const IconRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const ButtonRow = styled.div`
  display: flex;
  width: 275px;
  height: 60px;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: flex;
  padding: 16px 12px;
  justify-content: center;
  align-items: center;
  flex: 1;
  border-radius: 8px;
  background: var(--table-BG-table_bg02_hov, #f0f3fe);
  border: none;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 12px 8px;
  }
`;

const ButtonText = styled.span`
  padding: 0 16px;
  color: var(--Primary-brand_02, #4c84e3);
  font-size: 18px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SubTitle = styled.h3`
  align-self: flex-start;
  font-size: 32px;
  font-weight: 700;
  color: var(--gray-ui_gray02, #252525);
  margin-top: 10px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-top: 4px;
  }
`;

const RecommendationWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 120px;
  gap: 12px;
  text-align: start;

  @media (max-width: 768px) {
    padding-bottom: 80px;
    gap: 8px;
  }
`;

const RecommendItem = styled.div`
  display: flex;
  padding: 16px 12px;
  align-items: center;
  gap: 10px;
  width: 100%;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: #f0f3fe;
  }
`;

const LeftBox = styled.div`
  display: flex;
  width: 56px;
  padding-right: 16px;
  justify-content: center;
  align-items: center;
`;

const RecommendIndex = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-size: 18px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RightText = styled.div`
  flex: 1;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 14px;
    letter-spacing: -0.28px;
  }
`;

const StatusText = styled.div`
  width: 100%;
  text-align: center;
  padding: 100px 0;
  font-size: 16px;
  color: #999;
`;
